import React, { useState, useEffect } from "react";
import { Parallax } from "react-scroll-parallax";
import {
  FaFacebookF,
  FaLinkedinIn,
  FaInstagram,
  FaYoutube,
} from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { MdEmail, MdPhone } from "react-icons/md";

const Footer = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const getSpeed = (desktopSpeed) =>
    isMobile ? desktopSpeed * 0.3 : desktopSpeed;

  return (
    <footer
      className="relative flex flex-col justify-between px-4 sm:px-6 lg:px-0 border-t border-white/10 overflow-hidden"
      style={{
        paddingTop: isMobile ? "6rem" : "15rem",
        paddingBottom: isMobile ? "4rem" : "18rem",
        minHeight: isMobile ? "auto" : "924px",
      }}
    >
      {/* Left Glow Effect */}
      <Parallax
        speed={-25}
        className="absolute pointer-events-none"
        style={{
          left: "-300px",
          top: "30%",
          transform: "translateY(-50%)",
        }}
      >
        <div
          style={{
            width: isMobile ? "300px" : "500px",
            height: isMobile ? "400px" : "700px",
            background:
              "radial-gradient(circle, rgba(6, 182, 212, 0.25) 0%, rgba(6, 182, 212, 0.12) 40%, transparent 70%)",
            filter: `blur(${isMobile ? 25 : 40}px)`,
            opacity: 1.2,
          }}
        ></div>
      </Parallax>

      {/* Right Glow Effect */}
      <Parallax
        speed={25}
        className="absolute pointer-events-none"
        style={{
          right: "-300px",
          top: "40%",
          transform: "translateY(-50%)",
        }}
      >
        <div
          style={{
            width: isMobile ? "300px" : "500px",
            height: isMobile ? "400px" : "700px",
            background:
              "radial-gradient(circle, rgba(20, 184, 166, 0.2) 0%, rgba(20, 184, 166, 0.1) 40%, transparent 70%)",
            filter: `blur(${isMobile ? 15 : 20}px)`,
            opacity: 1.2,
          }}
        ></div>
      </Parallax>

      {/* Bottom Teal Glow */}
      <div
        className="absolute left-1/2 -translate-x-1/2 pointer-events-none"
        style={{
          top: isMobile ? "500px" : "800px",
          width: isMobile ? "1000px" : "1621px",
          height: isMobile ? "900px" : "1524px",
          background: "#009999",
          borderRadius: "80%",
          filter: `blur(${isMobile ? 80 : 120}px)`,
          opacity: 2,
        }}
      ></div>

      {/* Noise Overlay */}

      <div
        className="absolute pointer-events-none"
        style={{
          inset: 0,
          width: "100%",
          height: "120%",
          backgroundImage: "url(/images/noise.svg)",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          opacity: 0.58,
          mixBlendMode: "overlay",
        }}
      />

      {/* Content Container */}
      <div className="w-full max-w-[1920px] mx-auto">
        <div className="px-0 lg:px-16 xl:px-24">
          <div className="relative z-10 flex-grow">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-32 mb-12 sm:mb-20">
              {/* Left Section */}
              <div className="pl-0 lg:pl-10 text-center lg:text-left">
                <Parallax speed={getSpeed(18)} translateY={[-15, 15]}>
                  <h2
                    className="mb-8 sm:mb-12 leading-tight pl-0 lg:pl-16"
                    style={{
                      fontFamily: "Montserrat, sans-serif",
                      fontSize: "clamp(28px, 6vw, 60px)",
                      fontWeight: "400",
                      letterSpacing: "0.01em",
                      lineHeight: "1.2",
                    }}
                  >
                    Let's Build the
                    <br />
                    Future Together
                  </h2>
                </Parallax>

                <div>
                  <Parallax speed={getSpeed(5)} translateY={[-10, 10]}>
                    <p
                      className="pt-4 sm:pt-6 mb-8 sm:mb-12 leading-tight pl-0 lg:pl-16"
                      style={{
                        fontFamily: "Montserrat, sans-serif",
                        fontSize: "clamp(16px, 2vw, 24px)",
                        fontWeight: "400",
                        letterSpacing: "0.13em",
                      }}
                    >
                      Stay Connected With
                    </p>
                  </Parallax>

                  <Parallax speed={getSpeed(-6)} translateY={[12, -12]}>
                    <div className="flex justify-center lg:justify-start gap-4 flex-wrap pl-0 lg:pl-16">
                      {[
                        {
                          href: "https://facebook.com/heyareweare",
                          icon: <FaFacebookF className="w-5 h-5" />,
                          label: "Facebook",
                        },
                        {
                          href: "https://linkedin.com/company/heyareweare/",
                          icon: <FaLinkedinIn className="w-5 h-5" />,
                          label: "LinkedIn",
                        },
                        {
                          href: "https://www.instagram.com/heyareweare/",
                          icon: <FaInstagram className="w-5 h-5" />,
                          label: "Instagram",
                        },
                        {
                          href: "https://youtube.com/@heyareweare",
                          icon: <FaYoutube className="w-5 h-5" />,
                          label: "YouTube",
                        },
                        {
                          href: "https://x.com/heyareweare",
                          icon: <FaXTwitter className="w-5 h-5" />,
                          label: "YouTube",
                        },
                      ].map((item, idx) => (
                        <a
                          key={idx}
                          href={item.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white text-black flex items-center justify-center transition-transform hover:scale-110"
                          aria-label={item.label}
                        >
                          {item.icon}
                        </a>
                      ))}
                    </div>
                  </Parallax>
                </div>
              </div>

              {/* Right Section */}
              <div className="flex flex-col items-center lg:items-end mt-8 sm:mt-12 lg:mt-16 lg:pr-16">
                <Parallax speed={getSpeed(-12)} translateY={[18, -18]}>
                  <a
                    href="https://wa.me/919043126018"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="border border-white hover:bg-white hover:text-black transition-all mb-10 sm:mb-16 flex items-center justify-center"
                    style={{
                      width: isMobile ? "220px" : "283px",
                      height: isMobile ? "50px" : "58px",
                      borderRadius: "10px",
                      fontFamily: "Montserrat, sans-serif",
                      fontSize: isMobile ? "16px" : "18px",
                      fontWeight: "400",
                      padding: 0,
                    }}
                  >
                    Enquire Now →
                  </a>
                </Parallax>

                <Parallax speed={getSpeed(15)} translateY={[10, 22]}>
                  <div className="space-y-4 sm:space-y-6 text-left w-full lg:w-auto mt-8 sm:mt-10">
                    <p
                      style={{
                        fontFamily: "Montserrat, sans-serif",
                        fontSize: isMobile ? "20px" : "24px",
                        fontWeight: "400",
                        letterSpacing: "0.13em",
                        lineHeight: "26.8px",
                      }}
                    >
                      Contact Us
                    </p>

                    <a
                      href="tel:+919043126018"
                      className="flex justify-start items-center gap-3 text-white hover:text-gray-200 transition-colors"
                      style={{
                        fontSize: isMobile ? "14px" : "16px",
                        fontFamily: "Montserrat, sans-serif",
                        fontWeight: "400",
                      }}
                    >
                      <MdPhone className="w-5 h-5 flex-shrink-0" />
                      <span>+91 90431 26018</span>
                    </a>

                    <a
                      href="mailto:contactus@heyareweare.com"
                      className="flex justify-start items-center gap-3 text-white hover:text-gray-200 transition-colors"
                      style={{
                        fontSize: isMobile ? "14px" : "16px",
                        fontFamily: "Montserrat, sans-serif",
                        fontWeight: "400",
                      }}
                    >
                      <MdEmail className="w-5 h-5 flex-shrink-0" />
                      <span>contactus@heyareweare.com</span>
                    </a>
                  </div>
                </Parallax>
              </div>
            </div>
          </div>

          {/* Divider and Copyright */}
          <div
            className={`absolute left-0 w-full z-20 ${
              isMobile ? "bottom-2" : "bottom-10"
            }`}
          >
            <div
              className="w-3/4 mx-auto mb-4 sm:mb-6"
              style={{
                height: "1px",
                background: "rgba(255, 255, 255, 0.9)",
                opacity: 0.6,
              }}
            ></div>

            <p
              className="text-center font-normal px-4"
              style={{
                fontSize: isMobile ? "13px" : "16px",
                color: "rgba(255, 255, 255, 0.7)",
                fontFamily: "Montserrat, sans-serif",
              }}
            >
               © 2025 Cosmo Heyareweare Technologies Private Limited - Aysha Builder . All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
